import { defineStore } from 'pinia';
import { Dark } from 'quasar';
import { SetBrand } from '../factories';
import ApplicationFilters from "src/types/controls/application-filters";
import useIsMobile from '../functions/quasar/use-is-mobile';
import DealerUserSettings from 'src/types/dealer/dealer-user-settings';
import DealerSettings from 'src/types/dealer/dealer-settings';
import { DealerService } from 'src/services';
import KeyValuePair from 'src/types/shared/key-value-pair';
import { useAuthStore } from './auth-store';

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    brand: "Default",
    isDark: false,
    isHeaderRevealed: true,
    isAppTrackingTransparencyAllowed: false,
    contactNumber: "0161 524 0090",
    whatsAppNumber: "0161 524 0090",
    noStockNumber: "0161 524 0056",
    pcpContactNumber: "0161 524 0090",
    contactEmail: "dealers@carfinance247.co.uk",
    timeOfLastPoll: null as (Date | null),
    mobileOverride: false,
    showCreditInfo: false,
    selectedCustomerView: "pipeline",
    defaultCustomerFilters: {
      decision: [],
      status: [],
      createdBy: [],
      date: 30,
      text: ""
    } as ApplicationFilters,
    customerFilters: {
      decision: [],
      status: [],
      createdBy: [],
      date: 30,
      text: ""
    } as ApplicationFilters,
    userId: null as (number | null),
    userSettings : [
      { key: "firstContactSource", value: "dealer" },
      { key: "applicationDefaultTerm", value: "" },
      { key: "termsToShow", value: [24, 37, 49, 60] },
    ] as KeyValuePair[],
    dealerSettings : [
      { key: "preferredContactMethod", value: "phone" },
    ] as KeyValuePair[],
  }),
  getters: {
    isMobile: (state) => state.mobileOverride || useIsMobile() ? true : false, 
    isDebug: (state) => window.location.href.includes("localhost"),
    isProduction: (state) => process.env.HOST_ENVIRONMENT === "production",
    isLiveUrl(): boolean { return this.isProduction && !this.isDebug },
    customerFiltersApplied: (state) => {
      let c = 0;
      state.customerFilters.text !== state.defaultCustomerFilters.text ? c++ : null;
      (state.customerFilters.decision && state.customerFilters.decision.length === 0) ? null : (state.customerFilters.decision !== state.defaultCustomerFilters.decision) ? c++ : null;
      (state.customerFilters.status && state.customerFilters.status.length === 0) ? null : (state.customerFilters.status !== state.defaultCustomerFilters.status) ? c++ : null;
      (state.customerFilters.createdBy && state.customerFilters.createdBy.length === 0) ? null : (state.customerFilters.createdBy !== state.defaultCustomerFilters.createdBy) ? c++ : null;
      state.customerFilters.date !== state.defaultCustomerFilters.date ? c++ : null;
      return c;
    },
    firstContactSource: (state) => {
      return state.userSettings.find(x => x.key === "firstContactSource")?.value;
    },
    preferredContactMethod: (state) => {
      return state.dealerSettings.find(x => x.key === "preferredContactMethod")?.value;
    },
    applicationDefaultTerm: (state) => {
      return state.userSettings.find(x => x.key === "applicationDefaultTerm") ? state.userSettings.find(x => x.key === "applicationDefaultTerm")!.value : "";
    },
    termsToShow: (state) => {
      return state.userSettings.find(x => x.key === "termsToShow") ? 
      JSON.parse(state.userSettings.find(x => x.key === "termsToShow")!.value) as number[]: 
        [24, 37, 49, 60];
    }
  },
  actions: {
    resetUserSettings(){
      this.userSettings = [
        { key: "firstContactSource", value: "dealer" },
        { key: "applicationDefaultTerm", value: "" },
        { key: "termsToShow", value: JSON.stringify([24, 37, 49, 60]) }
      ]
    },
    resetDealerSettings(){
      this.dealerSettings = [
        { key: "preferredContactMethod", value: "phone" },
      ]
    },
    updateFirstContactSource(source: string) {
      let index = this.userSettings.findIndex(x => x.key === "firstContactSource");
      if(index > -1){
        this.userSettings[index].value = source;
      }else{
        this.userSettings.push({ key: "firstContactSource", value: source });
      }
      DealerService.updateUserSettings({ dealerUserId: this.userId!, userSettings: this.userSettings });
    },
    updatePreferredContactMethod(source: string, affiliateUserId: number) {
      let index = this.dealerSettings.findIndex(x => x.key === "preferredContactMethod");
      if(index > -1){
        this.dealerSettings[index].value = source;
      }else{
        this.dealerSettings.push({ key: "preferredContactMethod", value: source });        
      }
      DealerService.updateDealerSettings({ affiliateUserId: affiliateUserId, settings: this.dealerSettings });
    },
    updateApplicationDefaultTerm(term: string) {
      let index = this.userSettings.findIndex(x => x.key === "applicationDefaultTerm");
      if(index > -1){
        this.userSettings[index].value = term;
      }else{
        this.userSettings.push({ key: "applicationDefaultTerm", value: term });
      }
      DealerService.updateUserSettings({ dealerUserId: this.userId!, userSettings: this.userSettings });
    },
    updateTermsToShow(terms: number[]) {
      let index = this.userSettings.findIndex(x => x.key === "termsToShow");
      if(index > -1){
        this.userSettings[index].value = JSON.stringify(terms);
      }else{
        this.userSettings.push({ key: "termsToShow", value: JSON.stringify(terms) });
      }
      DealerService.updateUserSettings({ dealerUserId: this.userId!, userSettings: this.userSettings });
    },
    updateDealerUserSettings(settings: DealerUserSettings) {
      this.userSettings = settings.userSettings ?? [];
    },
    getDealerUserSettings(userId: number){
      this.userId = userId;
      DealerService.getUserSettings(userId).then(res => {
        if(!res.userSettings){
          res.userSettings = this.userSettings;
          DealerService.updateUserSettings(res);
        }
        this.userSettings = res.userSettings;
      }).catch(error => {
        console.log("no user settings");
      })
    },
    updateDealerSettings(settings: DealerSettings) {
      this.dealerSettings = settings.settings ?? [];
    },
    getDealerSettings(affiliateUserId: number){
      return new Promise<KeyValuePair[]>(async (resolve, reject) => {
        DealerService.getDealerSettings(affiliateUserId).then(res => {
          this.dealerSettings = res.settings;
          resolve(this.dealerSettings);
        }).catch(error => {
          console.log("no user settings");
          resolve(this.dealerSettings);
        })
      })
    },
    updateShowCreditInfo(val: boolean) {
      this.showCreditInfo = val;
    },
    updateMobileOverride(val: boolean) {
      this.mobileOverride = val;
    },
    resetCustomerFilters() {
      this.defaultCustomerFilters = {
        decision: [],
        status: [],
        createdBy: [],
        date: 30,
        text: ""
      } as ApplicationFilters;
      this.customerFilters = {
        decision: [],
        status: [],
        createdBy: [],
        date: 30,
        text: ""
      } as ApplicationFilters;
    },
    updateIsDark(isDark: boolean) {
      this.isDark = isDark;
      Dark.set(isDark);
    },
    updateIsHeaderRevealed(isRevealed: boolean) {
      this.isHeaderRevealed = isRevealed;
    },
    updateIsAppTrackingTransparencyAllowed(isAllowed: boolean) {
      this.isAppTrackingTransparencyAllowed = isAllowed;
    },
    updateBrand(brand: string) {
      this.brand = brand;
      SetBrand(brand);
    },
    updateTimeOfLastPoll(date: Date) {
      this.timeOfLastPoll = date;
    },
    updateCustomerView(view: string) {
      this.selectedCustomerView = view;
    },
  },
  persist: true
});
